import { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { setRumGlobalContext } from './rumContext';
import { isRumTelemetryActive } from './rumOperationTracker';

export async function hashUserId(sub: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(sub));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32);
}

export type RumUserIdentity = Readonly<{
  sub: string | null | undefined;
  name?: string | null;
  email?: string | null;
}>;

export function setRumUser({ sub, name, email }: RumUserIdentity): void {
  if (!isRumTelemetryActive()) return;
  if (!sub) {
    datadogRum.clearUser();
    return;
  }
  void hashUserId(sub).then((id) =>
    datadogRum.setUser({
      id,
      ...(name ? { name } : {}),
      ...(email ? { email } : {}),
    }),
  );
}

export function clearRumUser(): void {
  if (!isRumTelemetryActive()) return;
  datadogRum.clearUser();
}

export function setRumTenant(tenant: string | null | undefined): void {
  if (!tenant) return;
  setRumGlobalContext({ tenant });
  if (!isRumTelemetryActive()) return;
  datadogRum.setGlobalContextProperty('tenant', tenant);
}

let currentPanelContextId: string | null = null;

export function initPanelContext(): string {
  currentPanelContextId = crypto.randomUUID();
  setRumGlobalContext({ panel_context_id: currentPanelContextId });
  if (isRumTelemetryActive()) {
    datadogRum.setGlobalContextProperty('panel_context_id', currentPanelContextId);
  }
  return currentPanelContextId;
}

export function getPanelContextId(): string | null {
  return currentPanelContextId;
}

export function startRumView(name: string): void {
  if (!isRumTelemetryActive()) return;
  datadogRum.startView({ name });
}

export type UseRumIdentityParams = RumUserIdentity & {
  tenant?: string | null;
};

/** Wires Auth0 user + tenant into Datadog RUM when identity changes. */
export function useRumIdentity({ sub, name, email, tenant }: UseRumIdentityParams): void {
  useEffect(() => {
    setRumUser({ sub, name, email });
  }, [sub, name, email]);

  useEffect(() => {
    setRumTenant(tenant);
  }, [tenant]);
}

/** Manual RUM views — use when `trackViewsManually: true` in datadogRum.init(). */
export function useRumViewTracking(pathname: string): void {
  useEffect(() => {
    if (!pathname) return;
    startRumView(pathname);
  }, [pathname]);
}
